import leapLogo from "../../../../../../assets/leap-logo.png";
import '../Learner.css';
// Eagerly start loading the logo image
const logoPreload = new Image();
logoPreload.src = leapLogo;

interface CoverPageProps {
  learnerName: string;
  reportData?: any;
}

// Floating particle component
function Particle({ delay, size, left, top }: { delay: number; size: number; left: string; top: string }) {
  return (
    <div
      className="absolute rounded-full bg-cyan/60 animate-float-particle"
      style={{
        width: size,
        height: size,
        left,
        top,
        animationDelay: `${delay}s`,
        boxShadow: `0 0 ${size * 2}px hsla(var(--cyan), 0.4)`,
      }}
    />
  );
}

export function CoverPage({ learnerName ,reportData }: CoverPageProps) {
  // Generate random particles
  const particles = [
    { delay: 0, size: 3, left: "10%", top: "20%" },
    { delay: 1, size: 2, left: "25%", top: "60%" },
    { delay: 2, size: 4, left: "80%", top: "15%" },
    { delay: 0.5, size: 2, left: "70%", top: "70%" },
    { delay: 1.5, size: 3, left: "45%", top: "85%" },
    { delay: 2.5, size: 2, left: "15%", top: "45%" },
    { delay: 3, size: 3, left: "90%", top: "40%" },
    { delay: 0.8, size: 2, left: "55%", top: "25%" },
    { delay: 1.8, size: 4, left: "35%", top: "75%" },
    { delay: 2.2, size: 2, left: "85%", top: "55%" },
    { delay: 3.5, size: 3, left: "5%", top: "80%" },
    { delay: 0.3, size: 2, left: "60%", top: "10%" },
  ];

  return (
    <div className="report-page cover-bg flex items-center justify-center p-12 lg:p-20 overflow-hidden h-full">
      {/* Dramatic background elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Nebula/Aurora effects - more vibrant */}
        <div 
          className="absolute top-[10%] left-[5%] w-[600px] h-[400px] rounded-full animate-nebula-drift blur-3xl"
          style={{
            background: "radial-gradient(ellipse, hsla(260, 70%, 60%, 0.35) 0%, hsla(280, 80%, 50%, 0.2) 40%, transparent 70%)",
          }}
        />
        <div 
          className="absolute bottom-[15%] right-[10%] w-[500px] h-[350px] rounded-full animate-nebula-drift-alt blur-3xl"
          style={{
            background: "radial-gradient(ellipse, hsla(190, 100%, 60%, 0.3) 0%, hsla(175, 90%, 50%, 0.15) 40%, transparent 70%)",
            animationDelay: "5s",
          }}
        />
        <div 
          className="absolute top-[40%] right-[25%] w-[400px] h-[300px] rounded-full animate-nebula-drift blur-3xl"
          style={{
            background: "radial-gradient(ellipse, hsla(270, 80%, 55%, 0.25) 0%, hsla(260, 70%, 60%, 0.12) 50%, transparent 70%)",
            animationDelay: "10s",
          }}
        />
        
        {/* Large glowing orb - more vibrant */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full animate-pulse-soft"
          style={{
            background: "radial-gradient(circle, hsla(190, 100%, 60%, 0.25) 0%, hsla(175, 90%, 50%, 0.1) 40%, transparent 70%)",
          }}
        />
        {/* Accent orb - more visible */}
        <div 
          className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-80"
          style={{
            background: "radial-gradient(circle, hsla(260, 70%, 60%, 0.2) 0%, transparent 60%)",
          }}
        />
        {/* Grid overlay */}
        <div className="absolute inset-0 grid-pattern opacity-50" />
        
        {/* Floating particles */}
        {particles.map((particle, index) => (
          <Particle key={index} {...particle} />
        ))}
      </div>
      
      <div className="relative z-10 text-center max-w-4xl mx-auto">
        {/* Logo with animated rings */}
        <div className="relative w-44 h-44 mx-auto mb-16">
          {/* Outer rotating ring */}
          <div 
            className="absolute inset-[-24px] rounded-full border-2 border-cyan/30 animate-spin-slow"
            style={{
              borderStyle: "dashed",
            }}
          />
          {/* Middle rotating ring (reverse) */}
          <div 
            className="absolute inset-[-12px] rounded-full border border-cyan/40 animate-spin-slow-reverse"
          />
          {/* Inner ring border */}
          <div 
            className="absolute inset-[-4px] rounded-full border border-purple/50"
          />
          {/* Black background inside rings */}
          <div className="absolute inset-0 rounded-full bg-background animate-logo-bg-shift" />
          {/* Subtle inner glow */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple/10 via-transparent to-cyan/10" />
          {/* Pulsing glow ring */}
          <div className="absolute inset-[-6px] rounded-full bg-gradient-to-r from-cyan/20 via-purple/20 to-teal/20 blur-lg animate-pulse-glow" />
          {/* Logo */}
          <img 
            src={leapLogo} 
            alt="ECHO Logo" 
            className="relative w-full h-full object-contain p-4 rounded-full"
          />
        </div>
        
        {/* Main title */}
        <h1 className="text-9xl md:text-10xl lg:text-[10rem] font-extralight tracking-tight mb-6">
          <span className="bg-gradient-to-r from-cyan via-teal to-cyan bg-clip-text text-transparent glow-text">
            ECHO
          </span>
        </h1>
        
        {/* Animated accent line */}
        <div className="relative w-40 h-0.5 mx-auto mb-8">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan to-transparent" />
          <div 
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer"
            style={{ backgroundSize: "200% 100%" }}
          />
        </div>
        
        <h2 className="text-3xl md:text-4xl font-light tracking-[0.4em] text-foreground/90 uppercase mb-16">
           {reportData?.lenUsername}'s Performance
        </h2>
        
        {/* Subtitle */}
        <p className="text-2xl text-foreground/70 font-light max-w-full mx-auto leading-relaxed mb-20">
          Insights from your First Time People Leadership Journey
        </p>
        
        {/* Bottom tag */}
        <div className="relative inline-block">
          <div className="absolute -inset-1 bg-gradient-to-r from-cyan/20 via-teal/10 to-cyan/20 rounded-full blur-lg opacity-60" />
          <span className="relative inline-block px-8 py-4 text-base tracking-[0.25em] uppercase text-cyan border border-cyan/40 rounded-full bg-card/50 backdrop-blur-sm">
            Your Personalised Report
          </span>
        </div>
        
        {/* Decorative corner elements - positioned further out */}
        <div className="absolute top-4 left-4 w-16 h-16 border-l-2 border-t-2 border-cyan/30 rounded-tl-lg" />
        <div className="absolute top-4 right-4 w-16 h-16 border-r-2 border-t-2 border-cyan/30 rounded-tr-lg" />
        <div className="absolute bottom-20 left-4 w-16 h-16 border-l-2 border-b-2 border-cyan/30 rounded-bl-lg" />
        <div className="absolute bottom-20 right-4 w-16 h-16 border-r-2 border-b-2 border-cyan/30 rounded-br-lg" />
      </div>
    </div>
  );
}
